import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import Loader from '@/assets/branding/loader.svg';

const buttonVariants = cva(
  'select-none inline-flex items-center gap-2 justify-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-background shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        foreground: 'border border-foreground bg-foreground text-background',
        outline:
          'border border-border text-foreground hover:bg-foreground hover:text-background hover:border-foreground',
        outline2:
          'border border-border text-foreground hover:border-foreground',
        secondary:
          'bg-foreground-secondary/20 dark:bg-secondary text-foreground hover:bg-foreground-secondary/50 dark:hover:bg-secondary/80',
        green:
          'bg-green text-black-alt dark:text-vestgrey-800 shadow hover:bg-green/90 transition uppercase font-mono tracking-wider',
        red: 'bg-red text-black-alt dark:text-vestgrey-800 shadow hover:bg-red/90 uppercase font-mono tracking-wider',
        yellow:
          'bg-yellow text-black-alt dark:text-vestgrey-800 shadow hover:bg-yellow/90 uppercase font-mono tracking-wider',
        ghost: 'hover:bg-muted hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        contrast: 'bg-foreground text-background hover:bg-ring',
      },
      size: {
        default: 'text-base rounded-md px-4 py-2',
        sm: 'text-sm rounded-md px-4 py-2 [&_svg]:size-4',
        lg: 'text-base rounded-md font-medium px-5 py-2.5',
        xl: 'text-lg rounded-md font-medium px-6 py-3',
        icon: 'w-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, disabled, loading, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loading && 'relative text-transparent [&_*]:opacity-0',
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        <Slottable>{props.children}</Slottable>
        {loading && (
          <img
            src={Loader}
            className="absolute w-14 !opacity-100"
            alt="Loader"
            aria-hidden="true"
          />
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
